import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import FeedbackForm from '../components/FeedbackForm';
import {
  MessageSquarePlus,
  Mail,
  Phone,
  HelpCircle,
  BookOpen,
  ExternalLink,
} from 'lucide-react';

const FAQ_ITEMS = [
  {
    q: 'How do I upgrade my subscription?',
    a: 'Go to the Pricing page from the navigation bar and select the plan that fits your needs. You can upgrade or downgrade at any time.',
  },
  {
    q: 'Can I cancel my subscription?',
    a: 'Yes. You can cancel anytime from your dashboard. Your access continues until the end of your billing period.',
  },
  {
    q: 'How does AI content generation work?',
    a: 'Our AI generator uses your organization profile and program data to create personalized fundraising content including proposals, emails, and grant letters.',
  },
  {
    q: 'How do I share my Design Studio creations?',
    a: 'Open any saved design in Design Studio and click "Share". You\'ll get a link that stays active for 30 days.',
  },
  {
    q: 'Is my data secure?',
    a: 'Yes. All data is encrypted and stored securely. We never share your information with third parties.',
  },
];

export default function HelpPage() {
  const [expandedFaq, setExpandedFaq] = useState(null);

  return (
    <div className="min-h-screen bg-[#FAFAFA] pt-20 pb-16" data-testid="help-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-gradient-to-br from-primary to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg">
            <HelpCircle className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#0A0A0A] mb-3">
            Help & Support
          </h1>
          <p className="text-[#5C6B67] max-w-lg mx-auto">
            Find answers, submit feedback, or reach our team directly.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Left – FAQ + Contact */}
          <div className="lg:col-span-2 space-y-6">
            {/* Contact card */}
            <Card className="bg-white">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold text-stone-800 flex items-center gap-2">
                  <Mail className="w-4 h-4 text-primary" />
                  Contact Us
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 pt-0">
                <a
                  href="mailto:info@strategicfundraisingconsultingllc.com"
                  className="flex items-center gap-2 text-sm text-primary hover:underline"
                  data-testid="help-email-link"
                >
                  <Mail className="w-3.5 h-3.5" />
                  info@strategicfundraisingconsultingllc.com
                </a>
                <div className="pt-2 border-t border-stone-100">
                  <p className="text-xs text-[#5C6B67] mb-2">Need 1-on-1 guidance?</p>
                  <Button asChild variant="outline" size="sm" className="w-full" data-testid="help-consulting-link">
                    <Link to="/consulting">
                      <Phone className="w-3.5 h-3.5 mr-1.5" />
                      Book a Strategy Session
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* FAQ */}
            <Card className="bg-white">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold text-stone-800 flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-primary" />
                  Frequently Asked Questions
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-1">
                  {FAQ_ITEMS.map((item, idx) => (
                    <div key={idx} className="border-b border-stone-100 last:border-0">
                      <button
                        onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                        className="w-full text-left py-3 flex items-start justify-between gap-2"
                        data-testid={`faq-item-${idx}`}
                      >
                        <span className="text-sm font-medium text-stone-800">{item.q}</span>
                        <span className="text-stone-400 text-xs flex-shrink-0 mt-0.5">
                          {expandedFaq === idx ? '−' : '+'}
                        </span>
                      </button>
                      {expandedFaq === idx && (
                        <p className="text-sm text-[#5C6B67] pb-3 -mt-1 pr-4">{item.a}</p>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right – Feedback Form */}
          <div className="lg:col-span-3">
            <FeedbackForm />
          </div>
        </div>
      </div>
    </div>
  );
}
